import type { ComplimentaryCareEnrollment } from '../../api/types';
import { submissionKindLabel } from '../../components/enrollmentLabels';
import { EnrollmentStatusBadge } from '../../components/EnrollmentStatusBadge';
import { ReviewActionButtons } from './ReviewActions';
import type { ReviewAction } from './useReviewActions';

export type SortKey = 'employeeDisplayName' | 'hireDate' | 'submittedAtUtc' | 'status';

export interface EnrollmentTableProps {
  enrollments: ComplimentaryCareEnrollment[];
  emptyMessage: string;

  sortKey: SortKey;
  sortDescending: boolean;
  onSort: (key: SortKey) => void;

  onOpen: (enrollment: ComplimentaryCareEnrollment) => void;
  onAction: (enrollment: ComplimentaryCareEnrollment, action: ReviewAction) => void;
  busyId: string | null;
}

export function EnrollmentTable({
  enrollments,
  emptyMessage,
  sortKey,
  sortDescending,
  onSort,
  onOpen,
  onAction,
  busyId,
}: EnrollmentTableProps) {
  return (
    <div className="care-table__scroll">
      <table className="care-table care-table--fixed">
        {/*
          Fixed columns, declared once. Without them each table sizes its own columns from whatever
          rows it happens to hold, so the Pending and Reviewed tables never line up with each other and
          both jump every time a filter changes the rows — the Rejected tab worst of all, since the set
          there is usually small. These widths are the same for every filter and every table.
        */}
        <colgroup>
          <col className="care-col--employee" />
          <col className="care-col--enrollees" />
          <col className="care-col--date" />
          <col className="care-col--date" />
          <col className="care-col--status" />
          <col className="care-col--reviewer" />
          <col className="care-col--actions" />
        </colgroup>
        <thead>
          <tr>
            <SortableHeader label="Employee" columnKey="employeeDisplayName" activeKey={sortKey} descending={sortDescending} onSort={onSort} />
            <th>Enrollees</th>
            <SortableHeader label="Hire date" columnKey="hireDate" activeKey={sortKey} descending={sortDescending} onSort={onSort} />
            <SortableHeader label="Submitted" columnKey="submittedAtUtc" activeKey={sortKey} descending={sortDescending} onSort={onSort} />
            <SortableHeader label="Status" columnKey="status" activeKey={sortKey} descending={sortDescending} onSort={onSort} />
            <th>Reviewed by</th>
            <th className="care-table__actions-header">Actions</th>
          </tr>
        </thead>
        <tbody>
          {enrollments.map((enrollment) => (
            <EnrollmentRow
              key={enrollment.id}
              enrollment={enrollment}
              onOpen={onOpen}
              onAction={onAction}
              busy={busyId === enrollment.id}
            />
          ))}
        </tbody>
      </table>

      {enrollments.length === 0 && <p className="care-empty">{emptyMessage}</p>}
    </div>
  );
}

interface EnrollmentRowProps {
  enrollment: ComplimentaryCareEnrollment;
  onOpen: (enrollment: ComplimentaryCareEnrollment) => void;
  onAction: (enrollment: ComplimentaryCareEnrollment, action: ReviewAction) => void;
  busy: boolean;
}

function EnrollmentRow({ enrollment, onOpen, onAction, busy }: EnrollmentRowProps) {
  const dependents = enrollment.enrollees.filter((e) => e.relationship !== 'teamMember');
  const kindLabel = submissionKindLabel(enrollment);

  return (
    <tr
      className="care-table__row"
      // The whole row opens the detail view; the buttons in the last cell stop the click reaching here.
      onClick={() => onOpen(enrollment)}
      tabIndex={0}
      role="button"
      aria-label={`Open ${enrollment.employeeDisplayName}'s enrollment`}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(enrollment);
        }
      }}
    >
      <td>
        <div className="care-table__primary">{enrollment.employeeDisplayName}</div>
        <div className="care-table__muted">{enrollment.employeeUpn}</div>
        {/* An employee can appear twice on this page at once — a submission above and the row it
            would replace below — so each row has to say which it is, and a re-enrollment has to be
            distinguishable from an edit: the first closes a gap HR themselves created. */}
        {kindLabel && (
          <div className={enrollment.kind === 'reEnrollment' ? 'care-tag care-tag--attention' : 'care-tag'}>
            {kindLabel}
          </div>
        )}
        {enrollment.replacesDeactivatedAtUtc && (
          <div className="care-table__muted">
            Deactivated {new Date(enrollment.replacesDeactivatedAtUtc).toLocaleDateString()}
          </div>
        )}
      </td>
      <td>
        <div className="care-table__primary">{enrollment.enrollees.length}</div>
        <div className="care-table__muted">
          {dependents.length === 0
            ? 'Team member only'
            : dependents.map((d) => d.name.split(' ')[0]).join(', ')}
        </div>
      </td>
      <td>{enrollment.hireDate}</td>
      <td>{new Date(enrollment.submittedAtUtc).toLocaleDateString()}</td>
      <td>
        <EnrollmentStatusBadge status={enrollment.status} />
        {/* Only while it is still a decision to make — an approval clears the difference. */}
        {enrollment.status === 'pendingReview' && enrollment.changes.length > 0 && (
          <div className="care-table__muted">
            {enrollment.changes.length} change{enrollment.changes.length === 1 ? '' : 's'} to review
          </div>
        )}
      </td>
      <td>
        {enrollment.reviewedByUpn ? (
          <>
            {/* Truncated rather than wrapped: an address broken mid-word across two lines is harder
                to read than one with its tail cut off, and the full value is a hover away. */}
            <div className="care-table__primary care-table__ellipsis" title={enrollment.reviewedByUpn}>
              {enrollment.reviewedByUpn}
            </div>
            <div className="care-table__muted">
              {enrollment.reviewedAtUtc && new Date(enrollment.reviewedAtUtc).toLocaleDateString()}
            </div>
          </>
        ) : (
          <span className="care-table__muted">—</span>
        )}
      </td>
      <td>
        {/* The row itself opens the enrollment, so a click on a button must not also navigate. */}
        <div className="care-table__actions" onClick={(event) => event.stopPropagation()}>
          <ReviewActionButtons enrollment={enrollment} onAction={onAction} busy={busy} size="small" />
        </div>
      </td>
    </tr>
  );
}

interface SortableHeaderProps {
  label: string;
  columnKey: SortKey;
  activeKey: SortKey;
  descending: boolean;
  onSort: (key: SortKey) => void;
}

function SortableHeader({ label, columnKey, activeKey, descending, onSort }: SortableHeaderProps) {
  const isActive = activeKey === columnKey;

  return (
    <th aria-sort={isActive ? (descending ? 'descending' : 'ascending') : 'none'}>
      <button type="button" className="care-table__sort" onClick={() => onSort(columnKey)}>
        {label}
        <span className="care-table__sort-arrow" aria-hidden="true">
          {isActive ? (descending ? '▾' : '▴') : ''}
        </span>
      </button>
    </th>
  );
}
